import { Event } from './types';

export type MediaType = 'video' | 'gif' | 'image';

// Helper: unified poster selection honoring precedence
export function getEventPosterUrl(event: Event): string {
  // Precedence for poster-like images:
  // 1) Explicit event video poster
  if (event.video_poster?.url) return event.video_poster.url;
  // 2) Event image
  if (event.event_image?.url) return event.event_image.url;
  // 3) Theme image as last resort poster
  if (event.event_theme_id?.theme_fullimage?.url) return event.event_theme_id.theme_fullimage.url;
  return '';
}

export function getEventMediaType(event: Event): MediaType {
  // Precedence for active media rendering:
  // 1) Event video
  if (event.video_file?.url) return 'video';
  // 2) Event image, but only when user selected photo theme explicitly
  if (event.event_theme === 'photo' && event.event_image?.url) return 'image';
  // 3) Theme video
  if (event.event_theme_id?.theme_video?.url) return 'video';
  // 4) Theme GIF
  if (event.event_theme_id?.design_gif?.url) return 'gif';
  // 5) Theme image (default)
  return 'image';
}

export function getEventImage(event: Event): string {
  // For meta tags and static fallbacks, always return an image URL (never a video).
  // Use unified poster selection.
  const poster = getEventPosterUrl(event);
  if (poster) return poster;
  // As a final fallback, if a GIF exists and no poster, use it (still an image URL)
  if (event.event_theme_id?.design_gif?.url) return event.event_theme_id.design_gif.url;
  return '';
}

export function getEventVideoUrl(event: Event): string {
  // Video precedence: event video, else theme video
  return event.video_file?.url || event.event_theme_id?.theme_video?.url || '';
}

export function getEventGifUrl(event: Event): string {
  return event.event_theme_id?.design_gif?.url || '';
}

export function getMaxGuestCount(event: Event): number {
  // event_maxguests is the number of guests the primary invitee may bring.
  // The primary invitee always occupies one additional spot in the total party size.
  if (event.limit_additional_guest === true) {
    // Handle both number and string types for event_maxguests
    let maxGuests: number | undefined;
    
    if (typeof event.event_maxguests === 'number') {
      maxGuests = event.event_maxguests;
    } else if (typeof event.event_maxguests === 'string') {
      const parsed = parseInt(event.event_maxguests);
      maxGuests = isNaN(parsed) ? undefined : parsed;
    }
    
    // If maxGuests is defined (including 0), add the primary invitee to the total.
    if (maxGuests !== undefined) {
      return 1 + Math.max(0, Math.floor(maxGuests));
    }
    
    // If limit_additional_guest is true but no maxguests set, default to reasonable limit
    return 8;
  }
  
  // If limit_additional_guest is false, no limit
  return Number.MAX_SAFE_INTEGER;
}

export function validateGuestCount(event: Event, adultCount: number, childCount: number): { isValid: boolean; maxAllowed: number; errorMessage?: string } {
  const totalGuests = adultCount + childCount;
  const maxAllowed = getMaxGuestCount(event);
  
  if (totalGuests > maxAllowed) {
    const additionalAllowed = maxAllowed - 1; // Subtract 1 for the primary guest
    return {
      isValid: false,
      maxAllowed,
      errorMessage: additionalAllowed === 0
        ? "No additional guests are allowed for this event. Only the primary invitee can attend."
        : event.limit_additional_guest 
        ? `You can bring a maximum of ${additionalAllowed} additional guests (adults and children combined).`
        : `You can bring a maximum of ${additionalAllowed} additional guests for this event.`
    };
  }
  
  return { isValid: true, maxAllowed };
}

export function formatRsvpStatus(adultCount: number, childCount: number, hasChildren: boolean): string {
  const parts = [];
  
  if (adultCount === 1) {
    parts.push('1 Adult');
  } else if (adultCount > 1) {
    parts.push(`${adultCount} Adults`);
  }

  if (hasChildren && childCount > 0) {
    if (childCount === 1) {
      parts.push('1 Child');
    } else {
      parts.push(`${childCount} Children`);
    }
  }

  return parts.join(', ');
}

export function getAppUrl(inviteId?: string, eventId?: string): string {
  const baseUrl = 'https://in.invitd.us/sq6nusKPpZb';
  const params = new URLSearchParams();
  params.append('saveEvent', 'true');
  
  if (inviteId) {
    params.append('inviteID', inviteId);
  } else if (eventId) {
    params.append('eventID', eventId);
  }

  return `${baseUrl}?${params.toString()}`;
}
